import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Panel } from '@/components/ui/panel';
import { useDeleteJob } from '@/hooks/useJobs';
import { authClient } from '@/lib/auth-client';

interface JobDeleteCardProps {
  jobId: string;
  jobCode: string;
  onDelete?: () => void;
}

export function JobDeleteCard({
  jobId,
  jobCode,
  onDelete,
}: JobDeleteCardProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const deleteJob = useDeleteJob();
  const { data: session } = authClient.useSession();

  const handleDelete = async () => {
    if (!reason.trim()) return;

    try {
      await deleteJob.mutateAsync({
        id: jobId,
        reason: reason.trim(),
        userId: session?.user?.id,
      });
      setDeleteDialogOpen(false);
      setReason('');
      onDelete?.();
    } catch (error) {
      // Error is already handled by the mutation hook's onError
      console.error('Failed to delete job:', error);
    }
  };

  const handleDialogChange = (open: boolean) => {
    setDeleteDialogOpen(open);
    if (!open) {
      setReason('');
    }
  };

  return (
    <Panel title="Danger Zone">
      <Dialog open={deleteDialogOpen} onOpenChange={handleDialogChange}>
        <DialogTrigger asChild>
          <Button variant="destructive" className="w-full">
            Delete Job
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Job</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete job {jobCode}? The job will be moved to the "Deleted" bucket and can be viewed using the "Deleted Only" filter.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Reason for deletion <span className="text-red-500">*</span>
            </label>
            <Textarea
              placeholder="Enter reason for deletion..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDialogChange(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!reason.trim() || deleteJob.isPending}
            >
              {deleteJob.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
